import { useState, useEffect, useCallback } from 'react';

/**
 * Hook que calcula dinámicamente el maxHeight de un contenedor
 * midiendo las primeras N cards hijas + espaciado adicional.
 * Usa ResizeObserver para recalcular automáticamente cuando el contenido cambia.
 * 
 * @param {React.RefObject} containerRef - Ref del contenedor directo de las cards
 * @param {number} maxCards - Cantidad máxima de cards a medir para el cálculo
 * @param {Object} options - Opciones de espaciado
 * @param {number} options.gap - Gap entre cards (CSS gap)
 * @param {number} options.padding - Padding total del wrapper externo
 * @param {number} options.extraHeight - Altura extra (botón, márgenes, etc.)
 * @returns {number|null} Altura calculada o null si aún no se midió
 */
export const useCalculatedHeight = (containerRef, maxCards, { gap = 10, padding = 30, extraHeight = 50 } = {}) => {
  const [height, setHeight] = useState(null);

  const calculate = useCallback(() => {
    if (!containerRef.current) return;

    const cards = containerRef.current.children;
    if (cards.length === 0) return;

    const count = Math.min(cards.length, maxCards);
    let totalHeight = 0;

    for (let i = 0; i < count; i++) {
      totalHeight += cards[i].getBoundingClientRect().height;
    }

    // Sumar gaps entre cards + padding del wrapper + altura del botón
    totalHeight += (count - 1) * gap + padding + extraHeight;

    setHeight(totalHeight);
  }, [containerRef, maxCards, gap, padding, extraHeight]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    calculate();

    const observer = new ResizeObserver(calculate);
    observer.observe(container);

    return () => observer.disconnect();
  }, [calculate]);

  return height;
};
